import { useCallback, useState } from 'react';
import { IconUpload } from '@picsart/design-system/foundation/icons/IconUpload';
import { useTranslator } from '@picsart/rc/services/localization';
import UploadSourceMenu from '@/components/UploadSourceMenu';
import useStyles from './styles';
import {
  getInvalidTitle,
  getInvalidHint,
} from '@/utils/upload/getUploadErrorMessages';
import type { DropZoneProps } from './types';

const DropZone = ({
  accept,
  title,
  allowedExtensions,
  hasError,
  hasUploadError,
  showInvalidState,
  isDragging,
  onSelectFromDrive,
  onSelectFromCanvas,
  onSelectFromNativePicker,
  hasCanvasImages,
  onOpenFilePicker,
  onClearInvalidFeedback,
  onDrop,
  onDragOver,
  onDragEnter,
  onDragLeave,
}: DropZoneProps) => {
  const classes = useStyles({ hasError: hasError || hasUploadError });
  const translate = useTranslator();
  const [showSourceMenu, setShowSourceMenu] = useState(false);

  const handleClick = useCallback(() => {
    // Clear invalid state when user clicks to try again
    onClearInvalidFeedback();
    // On native mobile, use the native Picsart picker directly
    if (onSelectFromNativePicker) {
      onSelectFromNativePicker();
      return;
    }
    // If drive picker or canvas images are available, show source menu; otherwise open file input directly
    if (onSelectFromDrive || hasCanvasImages) {
      setShowSourceMenu(true);
    } else {
      onOpenFilePicker();
    }
  }, [
    onSelectFromNativePicker,
    onSelectFromDrive,
    hasCanvasImages,
    onOpenFilePicker,
    onClearInvalidFeedback,
  ]);

  const handleSelectFromDevice = useCallback(() => {
    onOpenFilePicker();
  }, [onOpenFilePicker]);

  const invalidTitle = getInvalidTitle({ hasUploadError, accept });
  const invalidHintText = getInvalidHint({ hasUploadError, allowedExtensions });

  const dropzoneElement = (
    <div
      className={`${classes.dropzone} ${
        showInvalidState
          ? classes.dropzoneInvalid
          : isDragging
          ? classes.dropzoneDragging
          : ''
      }`}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      role="button"
      tabIndex={0}
    >
      <div className={classes.wrapper}>
        <div className={classes.iconWrapper}>
          <IconUpload fill="currentColor" className={classes.icon} />
        </div>
        <span className={classes.text}>
          {showInvalidState
            ? translate(invalidTitle)
            : translate(title || 'Add your image')}
        </span>
        <span className={classes.hint}>
          {showInvalidState
            ? translate(invalidHintText)
            : translate(
                onSelectFromDrive || hasCanvasImages
                  ? 'Upload from device, Drive, or Canvas'
                  : 'Drop file here or tap to upload'
              )}
        </span>
      </div>
    </div>
  );

  return (
    <div className={classes.dropzoneWrapper}>
      {onSelectFromDrive || hasCanvasImages ? (
        <UploadSourceMenu
          reference={dropzoneElement}
          isOpen={showSourceMenu}
          onClose={() => setShowSourceMenu(false)}
          onSelectFromDevice={handleSelectFromDevice}
          onSelectFromDrive={onSelectFromDrive}
          onSelectFromCanvas={onSelectFromCanvas}
          hasCanvasImages={hasCanvasImages}
        />
      ) : (
        dropzoneElement
      )}
    </div>
  );
};

export default DropZone;
